import { Link, router } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { adminBrandPath, adminBrandsPath } from "@/routes"
import type { AdminAnalyticsTopTenant } from "@/types/admin-analytics"

/**
 * OKS-66 — Top-5 tenants table.
 *
 * Per UX Spec §2 — columns Brand · Active creators · Total memberships
 * · Created at · chevron. Whole row is the click target (cursor pointer,
 * `hover:bg-muted/50`), `role="link"`, navigates to
 * `adminBrandPath(id)`. Mobile reflows to vertical card list (rendered
 * via parallel `md:hidden` siblings) — matches OKS-63 best-performers
 * table reflow pattern.
 *
 * Empty state: when `tenants.length === 0` (no brand has ≥1 active
 * membership), card renders empty heading + body. UX Spec §3 ack.
 */
export interface AnalyticsTopTenantsTableProps {
  tenants: AdminAnalyticsTopTenant[]
}

export function AnalyticsTopTenantsTable({
  tenants,
}: AnalyticsTopTenantsTableProps) {
  const { t, i18n } = useTranslation()

  if (tenants.length === 0) {
    return (
      <Card data-testid="admin-analytics-top-tenants-table">
        <CardHeader>
          <CardTitle className="text-base font-semibold">
            {t("admin.analytics.top_tenants.title")}
          </CardTitle>
        </CardHeader>
        <CardContent
          className="text-muted-foreground flex flex-col gap-1 py-8 text-center text-sm"
          data-testid="admin-analytics-top-tenants-empty"
        >
          <p className="text-foreground text-sm font-medium">
            {t("admin.analytics.top_tenants.empty_title")}
          </p>
          <p className="max-w-md self-center">
            {t("admin.analytics.top_tenants.empty_body")}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card data-testid="admin-analytics-top-tenants-table">
      <CardHeader>
        <CardTitle className="text-base font-semibold">
          {t("admin.analytics.top_tenants.title")}
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          {t("admin.analytics.top_tenants.subtitle")}
        </p>
      </CardHeader>
      <CardContent>
        {/* Tabular layout — md+ */}
        <Table className="hidden md:table">
          <TableHeader>
            <TableRow>
              <TableHead>
                {t("admin.analytics.top_tenants.column.brand")}
              </TableHead>
              <TableHead className="text-right">
                {t("admin.analytics.top_tenants.column.active_creators")}
              </TableHead>
              <TableHead className="text-right">
                {t("admin.analytics.top_tenants.column.total_memberships")}
              </TableHead>
              <TableHead>
                {t("admin.analytics.top_tenants.column.created_at")}
              </TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenants.map((tenant) => (
              <TableRow
                key={tenant.id}
                role="link"
                tabIndex={0}
                aria-label={t("admin.analytics.top_tenants.row_aria", {
                  name: tenant.name,
                  active_creators: tenant.active_creators,
                  total_memberships: tenant.total_memberships,
                })}
                className="hover:bg-muted/50 focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none"
                onClick={() => router.visit(adminBrandPath(tenant.id))}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    router.visit(adminBrandPath(tenant.id))
                  }
                }}
                data-testid={`admin-analytics-top-tenants-row-${tenant.id}`}
              >
                <TableCell className="text-foreground font-medium">
                  {tenant.name}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {tenant.active_creators.toLocaleString()}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {tenant.total_memberships.toLocaleString()}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {formatDate(tenant.created_at, i18n.language)}
                </TableCell>
                <TableCell className="text-muted-foreground w-8">
                  <ChevronRight className="size-4" aria-hidden="true" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Card-list layout — mobile */}
        <ul className="flex flex-col gap-3 md:hidden" role="list">
          {tenants.map((tenant) => (
            <li key={tenant.id} role="listitem">
              <Link
                href={adminBrandPath(tenant.id)}
                className="hover:bg-muted/50 focus-visible:ring-ring block rounded-md border p-3 transition-colors focus-visible:ring-2 focus-visible:outline-none"
                data-testid={`admin-analytics-top-tenants-card-${tenant.id}`}
                aria-label={t("admin.analytics.top_tenants.row_aria", {
                  name: tenant.name,
                  active_creators: tenant.active_creators,
                  total_memberships: tenant.total_memberships,
                })}
              >
                <p className="text-foreground text-sm font-medium">
                  {tenant.name}
                </p>
                <dl className="text-muted-foreground mt-2 grid grid-cols-2 gap-1 text-xs">
                  <dt>
                    {t("admin.analytics.top_tenants.column.active_creators")}
                  </dt>
                  <dd className="text-foreground text-right tabular-nums">
                    {tenant.active_creators.toLocaleString()}
                  </dd>
                  <dt>
                    {t("admin.analytics.top_tenants.column.total_memberships")}
                  </dt>
                  <dd className="text-foreground text-right tabular-nums">
                    {tenant.total_memberships.toLocaleString()}
                  </dd>
                  <dt>{t("admin.analytics.top_tenants.column.created_at")}</dt>
                  <dd className="text-foreground text-right">
                    {formatDate(tenant.created_at, i18n.language)}
                  </dd>
                </dl>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-3 text-right">
          <Link
            href={adminBrandsPath()}
            className="text-foreground hover:text-foreground/80 text-xs font-medium underline-offset-4 hover:underline"
            data-testid="admin-analytics-top-tenants-view-all"
          >
            {t("admin.analytics.top_tenants.view_all")}
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

function formatDate(iso: string, locale: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
